// File: PortfolioPage.js
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Container, 
  Typography, 
  Box,
  Paper,
  Card,
  CardContent,
  Divider,
  Grid,
  Button,
  Alert,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  Checkbox,
  Link
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import UploadIcon from '@mui/icons-material/Upload';
import { 
  fetchPortfolio, 
  createPortfolio, 
  updatePortfolio,
  analyzePortfolio
} from '../../slices/portfolioSlice';
import { fetchCurrentUser } from '../../slices/userSlice';
import { Link as RouterLink } from 'react-router-dom';

// Import your input components
import ManualInput from './ManualInput';
import TextInput from './TextInput';
import SpreadsheetUpload from './SpreadsheetUpload';

function PortfolioPage() {
  const dispatch = useDispatch();
  const { data: portfolio, status, error } = useSelector((state) => state.portfolio);

  // Local state for the editing form (list of stocks, input mode, etc.)
  const [stocks, setStocks] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [updateError, setUpdateError] = useState('');
  const [totalValue, setTotalValue] = useState(0);
  const [inputMode, setInputMode] = useState('manual');
  const [tosAccepted, setTosAccepted] = useState(false);

  // On mount, fetch current user and portfolio data
  useEffect(() => {
    dispatch(fetchCurrentUser());
    dispatch(fetchPortfolio());
  }, [dispatch]);

  // When portfolio is fetched, sync local stocks state
  useEffect(() => {
    if (portfolio) {
      const stocksList = Object.entries(portfolio.portfolio).map(
        ([ticker, shares]) => ({
          ticker,
          shares: Number(shares),
        })
      );
      setStocks(stocksList);
      calculateTotalValue(stocksList);
    }
  }, [portfolio]);

  // Utility to recalc total shares
  const calculateTotalValue = (stocksList) => {
    const total = stocksList.reduce((sum, stock) => sum + stock.shares, 0);
    setTotalValue(total);
  };

  // Update the stocks state when the input form changes
  const handleStocksChange = (newStocks) => {
    console.log('handleStocksChange called with:', newStocks);
    setStocks(newStocks);
    calculateTotalValue(newStocks);
  };

  // Toggle between input modes (manual, text, spreadsheet)
  const handleInputModeChange = (event, newMode) => {
    if (newMode !== null) {
      setInputMode(newMode);
    }
  };

  // Handle form submission: if portfolio exists, update; otherwise, create.
  const handleSubmit = async () => {
    setIsSubmitting(true);
    setUpdateError('');
  
    try {
      const portfolioDict = stocks.reduce((acc, stock) => {
        acc[stock.ticker] = Number(stock.shares);
        return acc;
      }, {});
      console.log('Submitting portfolio:', portfolioDict);
      
      if (portfolio) {
        console.log('Updating existing portfolio');
        const result = await dispatch(updatePortfolio(portfolioDict)).unwrap();
        console.log('Update result:', result);
      } else {
        console.log('Creating new portfolio');
        const result = await dispatch(createPortfolio(portfolioDict)).unwrap();
        console.log('Create result:', result);
      }
      
      // Re-fetch the portfolio to update the UI
      dispatch(fetchPortfolio());
    } catch (err) {
      console.error('Error in handleSubmit:', err);
      setUpdateError(err.message || 'Failed to save portfolio');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle Analyze Portfolio
  const handleAnalyzePortfolio = () => {
    // Dispatch an action or navigate to the analysis page
    // Assuming you have an action called analyzePortfolio
    dispatch(analyzePortfolio()).then(() => {
      // Handle post-analysis actions if necessary
    }).catch((err) => {
      console.error('Error analyzing portfolio:', err);
      setUpdateError('Failed to analyze portfolio');
    });
  };

  // Render the appropriate input form based on the selected mode
  const renderInputMethod = () => {
    const commonProps = {
      initialStocks: stocks,
      onStocksChange: handleStocksChange,
      onSubmit: handleSubmit,
      showSubmitButton: false
    };

    switch (inputMode) {
      case 'manual':
        return <ManualInput {...commonProps} />;
      case 'text':
        return <TextInput {...commonProps} />;
      case 'spreadsheet':
        return <SpreadsheetUpload {...commonProps} />;
      default:
        return <ManualInput {...commonProps} />;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Top Section: Create/Edit Box */}
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          {portfolio ? 'Edit Your Portfolio' : 'Create Your Portfolio'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Enter your stock holdings to {portfolio ? 'update' : 'create'} your portfolio.
        </Typography>

        {/* Input Mode Toggle */}
        <ToggleButtonGroup
          value={inputMode}
          exclusive
          onChange={handleInputModeChange}
          aria-label="portfolio input mode"
          sx={{ width: '100%', mb: 3 }}
        >
          <ToggleButton value="manual" aria-label="manual input">
            <EditIcon sx={{ mr: 1 }} />
            Manual
          </ToggleButton>
          <ToggleButton value="text" aria-label="text input">
            <TextFieldsIcon sx={{ mr: 1 }} />
            Text
          </ToggleButton>
          <ToggleButton value="spreadsheet" aria-label="spreadsheet upload">
            <UploadIcon sx={{ mr: 1 }} />
            Spreadsheet
          </ToggleButton>
        </ToggleButtonGroup>

        {/* Action Buttons at the Top */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleAnalyzePortfolio}
              disabled={isSubmitting || stocks.length === 0 || !tosAccepted}
            >
              Analyze Portfolio
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isSubmitting || stocks.length === 0}
            >
              {isSubmitting ? <CircularProgress size={24} /> : (portfolio ? 'Save Changes' : 'Create Portfolio')}
            </Button>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
            <Checkbox
              checked={tosAccepted}
              onChange={(e) => setTosAccepted(e.target.checked)}
              id="tos-checkbox-portfolio"
            />
            <Typography variant="body2" color="text.secondary">
              I agree to the <Link to="/terms" target="_blank" component={RouterLink}>Terms of Service</Link>
            </Typography>
          </Box>
        </Box>

        {/* Render the chosen input method */}
        {renderInputMethod()}

        {/* Error message if any */}
        {(error || updateError) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error || updateError}
          </Alert>
        )}
      </Paper>

      {/* Bottom Section: Portfolio Details (only if a portfolio exists) */}
      {status === 'loading' ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      ) : portfolio ? (
        <Card elevation={2}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Your Portfolio</Typography>
              {/* No need for an external edit button since the top box is always visible */}
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              {Object.entries(portfolio.portfolio).map(([ticker, shares]) => (
                <Grid item xs={12} sm={6} md={4} key={ticker}>
                  <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {ticker}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {shares} shares
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
            <Box sx={{ mt: 3 }}>
              <Typography variant="caption" color="text.secondary">
                Last updated:{' '}
                {portfolio.updated_at ? new Date(portfolio.updated_at).toLocaleDateString() : 'Never'}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ) : (
        // If no portfolio exists, you might want to show a simple prompt here.
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">
            No portfolio found. Enter your holdings above to create your portfolio.
          </Typography>
        </Box>
      )}
    </Container>
  );
}

export default PortfolioPage;
